"use client";

import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { useBoard } from "@/components/BoardProvider";
import { Button } from "@/components/ui/button";

/**
 * Public form for submitting a question to the FAQ. Prefills the exam/module
 * from the current board (tmua.fyi vs esat.fyi) but lets the user change them.
 */
export function SubmitQuestionForm() {
  const board = useBoard();
  const submit = useMutation(api.submissions.submit);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [exam, setExam] = useState(board.shortName);
  const [module, setModule] = useState("");
  const [email, setEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modules for the currently-selected exam, so the dropdown stays relevant.
  const modules = useMemo(
    () => (exam === board.shortName ? board.modules.map((m) => m.shortName) : []),
    [exam, board],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await submit({
        title,
        description,
        exam,
        email,
        module: module || undefined,
      });
      setSubmitted(true);
      setTitle("");
      setDescription("");
      setModule("");
      setEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto rounded-lg border border-[var(--border)] bg-[var(--card)] px-6 py-8 text-center">
        <h3 className="text-2xl font-script text-[var(--foreground)] mb-2">Thanks for your question!</h3>
        <p className="text-[var(--muted-foreground)] font-polysans">
          We&apos;ve received your submission and will review it soon.
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-6"
          onClick={() => setSubmitted(false)}
        >
          Submit another question
        </Button>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-[var(--foreground)] " +
    "placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]";

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-5">
      <div className="text-center space-y-2">
        <h3 className="text-2xl md:text-3xl font-script text-[var(--foreground)]">
          Can&apos;t find your answer?
        </h3>
        <p className="text-[var(--muted-foreground)] font-polysans">
          Submit a question and we&apos;ll add it to the FAQ.
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="title" className="block text-sm font-polysans text-[var(--foreground)]">
          Title
        </label>
        <input
          id="title"
          type="text"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="A short summary of your question"
          className={inputClass}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="description" className="block text-sm font-polysans text-[var(--foreground)]">
          Description
        </label>
        <textarea
          id="description"
          required
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Give us the full details of what you'd like to know"
          className={`${inputClass} resize-y`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="exam" className="block text-sm font-polysans text-[var(--foreground)]">
            Exam
          </label>
          <select
            id="exam"
            value={exam}
            onChange={(e) => {
              setExam(e.target.value);
              setModule("");
            }}
            className={inputClass}
          >
            <option value="TMUA">TMUA</option>
            <option value="ESAT">ESAT</option>
          </select>
        </div>

        <div className="space-y-1">
          <label htmlFor="module" className="block text-sm font-polysans text-[var(--foreground)]">
            Module <span className="text-[var(--muted-foreground)]">(optional)</span>
          </label>
          {modules.length > 0 ? (
            <select
              id="module"
              value={module}
              onChange={(e) => setModule(e.target.value)}
              className={inputClass}
            >
              <option value="">Any / not sure</option>
              {modules.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="module"
              type="text"
              value={module}
              onChange={(e) => setModule(e.target.value)}
              placeholder="e.g. Physics"
              className={inputClass}
            />
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="email" className="block text-sm font-polysans text-[var(--foreground)]">
          Email
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={inputClass}
        />
        <p className="text-xs text-[var(--muted-foreground)]">
          So we can let you know when your question is answered. We won&apos;t share it.
        </p>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
        {submitting ? "Submitting…" : "Submit question"}
      </Button>
    </form>
  );
}
